import { Box, MenuList, Paper, styled, Link } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps, accordionSummaryClasses } from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import Typography from '@mui/material/Typography';
import TriangleIcon from '@components/Icon/Triangle';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useLocation } from 'react-router-dom';

import MenuItem from '@mui/material/MenuItem';

interface SettingsLayoutProps {
    children: React.ReactNode;
}

const ContainerWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '16px',
    height: '100%',
    minHeight: 'calc(100dvh - 139px)',
}));

const MenuContainer = styled(Paper)(({ theme }) => ({
    padding: '24px 12px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    height: '100%',
    borderRadius: '16px',
    background: '#222426',
    width: '237px',
}));

const ChildrenContainer = styled(Box)(({ theme }) => ({
    flexGrow: 1,
}));

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme }) => ({
        border: `none`,
        background: 'transparent',
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&::before': {
            display: 'none',
        },
    }),
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
    background: 'transparent !important',
    flexDirection: 'row-reverse',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.neutralLightGrey.main,
    border: 'none',
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
        transform: 'rotate(180deg)',
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
    ...theme.applyStyles('dark', {
        backgroundColor: 'rgba(255, 255, 255, .05)',
    }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: '0px 16px',
    marginTop: 0,
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    padding: '12px 10px',
    borderRadius: '12px',
    color: '#FFF',
    fontWeight: 400,
    fontSize: '16px',
    '&.Mui-selected': {
        backgroundColor: '#3C2DE14D',
    },
    '&:hover': {
        backgroundColor: '#3C2DE14D',
    },
}));

const menuList = [
    {
        id: 1,
        label: 'Profile Information',
        childs: [
            {
                id: 'a',
                label: 'Photo de profil',
                selected: 'photo',
                path: '/settings/photo',
            },
            {
                id: 'b',
                label: 'Profil',
                selected: 'profile',
                path: '/settings/profile',
            },
            {
                id: 'c',
                label: 'Recherche et savoir-faire',
                selected: 'research',
                path: '/settings/research',
            },
            {
                id: 'd',
                label: 'À Soft Skill',
                selected: 'skill',
                path: '/settings/skill',
            },
            {
                id: 'e',
                label: 'Formation',
                selected: 'formation',
                path: '/settings/formation',
            },
            {
                id: 'f',
                label: 'Expérience',
                selected: 'experience',
                path: '/settings/experience',
            },
            {
                id: 'g',
                label: 'Certifications',
                selected: 'certification',
                path: '/settings/certification',
            },
        ],
    },
    {
        id: 2,
        label: 'Documents',
        childs: [],
    },
    {
        id: 3,
        label: 'Security',
        childs: [],
    },
];

const SettingsLayout = ({ children }: SettingsLayoutProps) => {
    const location = useLocation();
    const { pathname } = location;

    return (
        <ContainerWrapper>
            <MenuContainer>
                {menuList.map(item => (
                    <Accordion key={item.id}>
                        <AccordionSummary
                            expandIcon={<TriangleIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header">
                            <Typography component="span">{item.label}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <MenuList>
                                {item.childs.map(child => (
                                    <Link key={child.id} href={child.path} underline="none">
                                        <CustomMenuItem
                                            key={child.id}
                                            selected={pathname.split('/').slice(1)[1] === child.selected}>
                                            <Typography>{child.label}</Typography>
                                        </CustomMenuItem>
                                    </Link>
                                ))}
                            </MenuList>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </MenuContainer>
            <ChildrenContainer>{children}</ChildrenContainer>
        </ContainerWrapper>
    );
};

export default SettingsLayout;
